import React from "react";
import { groupBy, keyBy, partition, sortBy } from "lodash";
import { useTranslation } from "react-i18next";
import { FLOODZONES } from "common/utils/maps/index";
import { generateDisplayName } from "common-client/utils/mapLayers";
import { orderPropertyWarningDefinitions } from "common-client/utils/warnings";
import {
  AccountPropertyWarningDefinition,
  CustomObjectGroup,
  PropertyWarningGroup,
  Raster,
  RasterLayerSection,
} from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { CustomMap } from "../Authorization/types";
import Icon, { ICON_COLORS, Icons } from "../Common/Icons";
import { Icon as LucideIcon } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import { AccountDocumentTypes } from "./__queries__/map";
import { LayerIcon } from "./__styles__/Layer";
import { LegendItem, LegendSymbol } from "./__styles__/LayerControl";
import Layer from "./Layer";
import LayerGroup from "./LayerGroup";
import { BaseMap, FIRM, LayerContext, SavedView } from "./layers";
import { group } from "./layers/savedViews";
import LayerSection from "./LayerSection";

const FDEM_ACCOUNT_ID = "ce0b1dc1-a2c0-40c0-be15-dcafd4c9cd58";

const LegendLabel = ({
  label,
  nested = false,
}: {
  label: string;
  nested?: boolean;
}) => {
  return (
    <span style={{ maxWidth: nested ? "158px" : "178px" }}>
      {/* Width of panel minus padding, minus space needed for the LegendSymbol */}
      <Body color="contentSecondary" size="small" type="regular">
        {label}
      </Body>
    </span>
  );
};

const CustomMapLegend = ({
  legend,
  nested = false,
}: {
  legend: NonNullable<CustomMap["legend"]>;
  nested?: boolean;
}) => {
  return (
    <React.Fragment>
      {legend.map(({ color, label }) => {
        const transparent = color.toUpperCase() === "#FFFFFF";
        return (
          <LegendItem key={label}>
            <LegendSymbol
              style={{ backgroundColor: color }}
              transparent={transparent}
            />
            <LegendLabel label={label} nested={nested} />
          </LegendItem>
        );
      })}
    </React.Fragment>
  );
};

const FloodZonesLegend = ({ isPublic }: { isPublic: boolean }) => {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      {FLOODZONES.map(
        ({ color, pattern, zones, label, transparent = false }) => {
          const finalLabel = t("flood-layers-zone", {
            zones: label ?? zones.join(", "),
          });

          return (
            <LegendItem key={color}>
              <LegendSymbol
                pattern={isPublic ? pattern : undefined}
                style={{ backgroundColor: color }}
                transparent={transparent}
              />
              <LegendLabel label={finalLabel} />
            </LegendItem>
          );
        },
      )}
      <LegendItem>
        <LegendSymbol floodway />
        <LegendLabel label={t("flood-layer-floodway")} />
      </LegendItem>
      <LegendItem>
        <LegendSymbol limwa />
        <LegendLabel label={t("flood-layer-limwa")} />
      </LegendItem>
    </React.Fragment>
  );
};

const TerrainLegend = () => {
  const { t } = useTranslation();
  return (
    <LegendItem>
      <LayerIcon>
        <LucideIcon
          iconName="square-dashed"
          color="contentPlaceholder"
          strokeWidth={3}
        />
      </LayerIcon>
      <LegendLabel label={t("land-layers-contour-lines-expanded")} />
    </LegendItem>
  );
};

const CBRSLegend = () => {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <LegendItem>
        <LegendSymbol cbrs />{" "}
        <LegendLabel label={t("flood-layers-cbrs-expanded")} />
      </LegendItem>
    </React.Fragment>
  );
};

const categorizeCustomMaps = ({
  objectCustomMaps,
  customObjectGroups,
}: {
  objectCustomMaps: CustomMap[];
  customObjectGroups: CustomObjectGroup[];
}) => {
  const nonGroupedCustomObjects: CustomMap[] = [];
  const groupedCustomObjects: Record<CustomObjectGroup["id"], CustomMap[]> = {};

  objectCustomMaps.forEach(customMap => {
    const customObjectGroupsForCustomMap = customObjectGroups.filter(
      group =>
        group.customMapIds.includes(customMap.id) &&
        // we only want to add a group if it has more than one map
        group.customMapIds.length > 1,
    );
    if (customObjectGroupsForCustomMap.length > 0) {
      customObjectGroupsForCustomMap.forEach(group => {
        groupedCustomObjects[group.id] ||= [];
        groupedCustomObjects[group.id]!.push(customMap);
      });
    } else {
      nonGroupedCustomObjects.push(customMap);
    }
  });

  return { nonGroupedCustomObjects, groupedCustomObjects };
};

interface LayerControlProps {
  firms: Array<FIRM>;
  rasters: Array<Raster>;
  baseMaps: Array<BaseMap>;
  accountDocumentTypes: AccountDocumentTypes;
  accountPropertyWarningDefinitions: Array<AccountPropertyWarningDefinition>;
  savedViews?: Array<SavedView>;
  disableRasterToggle?: boolean;
  disableFirmToggle?: boolean;
}
const LayerControl = ({
  firms,
  rasters,
  baseMaps,
  accountDocumentTypes,
  accountPropertyWarningDefinitions,
  savedViews,
  disableRasterToggle,
  disableFirmToggle,
}: LayerControlProps) => {
  const { t } = useTranslation();
  const { visibleFIRM: getVisibleFIRM } = React.useContext(LayerContext);
  const { account, isPublic } = React.useContext(AuthContext);
  const customMaps = account?.customMaps ?? [];

  const keyToTitleMap = {
    [PropertyWarningGroup.FIRM_WARNING]: "Firm warnings",
    [PropertyWarningGroup.CUSTOM_WARNING]: t("warnings-custom-category"),
    [PropertyWarningGroup.COMPLIANCE_WARNING]: t(
      "warnings-compliance-category",
    ),
    [PropertyWarningGroup.IMPROVEMENT_WARNING]: t("warnings-sisd-category"),
    [PropertyWarningGroup.PRELIMINARY_DAMAGE_ASSESSMENT]: t(
      "warnings-damage-category",
    ),
    [PropertyWarningGroup.REPETITIVE_LOSS]: "Repetitive Loss warnings",
  };

  // Yes, we intentionally want to hide FIRM warnings.
  // FIRM-based warnings are weird because they apply to every parcel, even those without properties or certificates,
  // and so we can't let folks toggle them on because they'd expect to see FIRM warnings even on those parcels that don't have properties
  // (but you need properties to have warnings).
  // An example FIRM warning for which this applies is the SFHA_MAPPED_IN warning, which says that a property is mapped in to the special flood hazard area.
  // Since every A zone is a SFHA, you'd expect to see a warning dot on every parcel in an A zone, which we can't do since not ever parcel will have a property.
  // That being said, we *still* want to show any custom warnings that might've been associated with the FIRM group,
  // so we check the `isCustom` flag as well.
  const filteredWarningDefinitions = accountPropertyWarningDefinitions.filter(
    accountPropertyWarningDefinition =>
      accountPropertyWarningDefinition.isActive &&
      (accountPropertyWarningDefinition.group !==
        PropertyWarningGroup.FIRM_WARNING ||
        accountPropertyWarningDefinition.isCustom),
  );

  const warningDefinitionsbyGroup = filteredWarningDefinitions.reduce(
    (grouped, warningDefinition) => {
      const key = warningDefinition.group;
      grouped[key] ||= [];
      grouped[key].push(warningDefinition);
      return grouped;
    },
    {} as Record<PropertyWarningGroup, AccountPropertyWarningDefinition[]>,
  );

  const groupedRasterLayers = groupBy(
    sortBy(rasters, "layerName"),
    "layerSection",
  );

  const showFloodLayerSection =
    firms.length > 0 || !!groupedRasterLayers[RasterLayerSection.FLOOD]?.length;

  const customObjectGroups = account?.customObjectGroups ?? [];

  const groupsById = keyBy(customObjectGroups, "id");

  const [objectCustomMaps, nonObjectCustomMaps] = partition(
    customMaps,
    ({ isObject }) => isObject,
  );

  const { nonGroupedCustomObjects, groupedCustomObjects } =
    categorizeCustomMaps({
      objectCustomMaps,
      customObjectGroups,
    });

  const showCustomLayerSection =
    nonObjectCustomMaps.length > 0 ||
    !!groupedRasterLayers[RasterLayerSection.CUSTOM]?.length;

  const visibleFIRM = getVisibleFIRM();

  const customLayerTitle =
    account?.id === FDEM_ACCOUNT_ID ?
      t("fdem-layers")
    : t("map-layers-category-custom-layers");

  const SEVERITY_TO_COLOR = {
    low: ICON_COLORS.GREY,
    medium: ICON_COLORS.YELLOW,
    high: ICON_COLORS.RED,
  };

  return (
    <div>
      <LayerSection
        title={t("map-layers-category-base-layers")}
        testId="base-map-toggle-group"
      >
        {baseMaps.map(({ name, id }) => (
          <Layer name={name} group="baseMaps" id={id} key={id} />
        ))}
      </LayerSection>
      {accountDocumentTypes.length > 0 && (
        <LayerSection title={t("common-files")} testId="documents-toggle-group">
          {accountDocumentTypes.map(accountDocumentType => (
            <Layer
              name={accountDocumentType.name}
              group="documents"
              id={accountDocumentType.id}
              key={accountDocumentType.id}
              color={accountDocumentType.color}
            />
          ))}
        </LayerSection>
      )}
      {filteredWarningDefinitions.length > 0 && (
        <LayerSection
          title={t("property-warnings-header")}
          testId="warnings-toggle-group"
        >
          {Object.keys(warningDefinitionsbyGroup).map(key => {
            const castedKey = key as PropertyWarningGroup;
            return (
              <LayerGroup
                title={keyToTitleMap[castedKey]}
                sectionName="warnings"
                key={key}
                testId={`${key}-toggle-group`}
                group={`${castedKey}Warnings`}
              >
                {orderPropertyWarningDefinitions(
                  warningDefinitionsbyGroup[castedKey],
                ).map(accountPropertyWarningDefinition => (
                  <Layer
                    nested={true}
                    name={accountPropertyWarningDefinition.title}
                    ariaLabel={`${accountPropertyWarningDefinition.severity} warning severity - ${accountPropertyWarningDefinition.title}`}
                    group={`${castedKey}Warnings`}
                    id={accountPropertyWarningDefinition.id}
                    key={accountPropertyWarningDefinition.id}
                    Icon={() => (
                      <Icon
                        icon={Icons.INFORMATION_CIRCLE}
                        color={
                          SEVERITY_TO_COLOR[
                            accountPropertyWarningDefinition.severity
                          ]
                        }
                        aria-label={accountPropertyWarningDefinition.severity}
                      />
                    )}
                  />
                ))}
              </LayerGroup>
            );
          })}
        </LayerSection>
      )}
      <LayerSection
        title={t("map-layers-category-objects")}
        testId="objects-toggle-group"
      >
        <Layer
          name={t("objects-properties")}
          group="buildings"
          id="properties"
          Icon={() => (
            <LucideIcon
              iconName="circle"
              color="contentPlaceholder"
              strokeWidth={3}
            />
          )}
        />

        {nonGroupedCustomObjects.map(({ name, id, legend }) => {
          return (
            <Layer name={name} group="customMaps" id={id} key={id}>
              {!!legend?.length && <CustomMapLegend legend={legend} />}
            </Layer>
          );
        })}
        {Object.entries(groupedCustomObjects).map(
          ([groupId, customObjects]) => {
            return (
              <LayerGroup
                title={groupsById[groupId]?.name ?? ""}
                sectionName="customObjects"
                key={groupId}
                testId={`${groupId}-toggle-group`}
                group={"customObjectGroups"}
                id={groupId}
              >
                {customObjects.map(({ name, id, legend }) => {
                  return (
                    <Layer
                      name={name}
                      group={"customMaps"}
                      id={id}
                      key={id}
                      nested={true}
                    >
                      {!!legend?.length && (
                        <CustomMapLegend legend={legend} nested={true} />
                      )}
                    </Layer>
                  );
                })}
              </LayerGroup>
            );
          },
        )}
      </LayerSection>
      {showFloodLayerSection && (
        <LayerSection
          title={t("map-layers-category-flood-layers")}
          testId="flood-layers-group"
        >
          {firms.map(({ name, id }) => {
            return (
              <Layer
                name={name}
                group="firms"
                id={id}
                key={id}
                disabled={disableFirmToggle}
              >
                <FloodZonesLegend isPublic={isPublic} />
              </Layer>
            );
          })}
          {!!visibleFIRM && (
            <Layer
              name={t("flood-layers-panels")}
              group="panels"
              id={visibleFIRM.id}
              key="panels"
            />
          )}

          {visibleFIRM?.components.baseFloodElevations.hasGeometries && (
            <Layer
              name={t("flood-layer-base-flood-elevation")}
              group="baseFloodElevations"
              id={visibleFIRM.id}
              key="baseFloodElevations"
            />
          )}
          {visibleFIRM?.components.crossSections.hasGeometries && (
            <Layer
              name={t("flood-layer-cross-sections")}
              group="crossSections"
              id={visibleFIRM.id}
              key="crossSections"
            />
          )}
          {visibleFIRM?.components.profileBaselines.hasGeometries && (
            <Layer
              name={t("flood-layers-profile-baselines")}
              group="profileBaselines"
              id={visibleFIRM.id}
              key="profileBaselines"
            />
          )}

          {groupedRasterLayers[RasterLayerSection.FLOOD]?.map(
            ({ layerName, id }) => {
              return (
                <Layer
                  name={layerName}
                  group="rasters"
                  id={id}
                  key={id}
                  disabled={disableRasterToggle}
                />
              );
            },
          )}
        </LayerSection>
      )}
      {showCustomLayerSection && (
        <LayerSection title={customLayerTitle} testId="custom-map-toggle-group">
          {nonObjectCustomMaps.map(({ name, id, legend }) => {
            return (
              <Layer
                name={t("custom-map-layer", { name })}
                group="customMaps"
                id={id}
                key={id}
              >
                {!!legend?.length && <CustomMapLegend legend={legend} />}
              </Layer>
            );
          })}
          {groupedRasterLayers[RasterLayerSection.CUSTOM]?.map(
            ({ layerName, id }) => {
              return (
                <Layer
                  name={layerName}
                  group="rasters"
                  id={id}
                  key={id}
                  disabled={disableRasterToggle}
                />
              );
            },
          )}
        </LayerSection>
      )}
      <LayerSection
        title={t("map-layers-category-land-layers")}
        testId="land-toggle-group"
      >
        <Layer name={t("flood-layers-cbrs")} group="topology" id="cbrs">
          <CBRSLegend />
        </Layer>

        <Layer
          name={t("land-layers-contour-lines")}
          group="topology"
          id="contour"
        >
          <TerrainLegend />
        </Layer>
        {groupedRasterLayers[RasterLayerSection.LAND]?.map(
          ({ layerName, id }) => {
            return (
              <Layer
                name={layerName}
                group="rasters"
                id={id}
                key={id}
                disabled={disableRasterToggle}
              />
            );
          },
        )}
      </LayerSection>
      {!!savedViews?.length && (
        <LayerSection title="Saved views" testId="saved-views-group">
          {savedViews.map(({ id, name, query }) => {
            return (
              <Layer
                name={generateDisplayName({ name, query, customObjectGroups })}
                group={group}
                id={id}
                key={id}
              />
            );
          })}
        </LayerSection>
      )}
    </div>
  );
};

export default LayerControl;
